import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { ProgressSpinner } from "primereact/progressspinner";
import type {
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapGeometry,
  BannerSwapOverlapResolution,
} from "../../../utils/@types";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import { formatSwapDateCell } from "../../BannerDashboard/bannerDashboardUtils";
import {
  OPTION_CHIP,
  OverlapConflictPreview,
  OverlapResolutionDiagram,
} from "./OverlapResolutionDiagram";
import { buildOverlapResolutionCallouts } from "./overlapResolutionCalloutUtils";
import OverlapResolutionCalloutList from "./OverlapResolutionCalloutList";
import "./OverlapResolutionDiagram.css";
import "../../BannerDashboard/bannerSwapDialogs.css";

export type SwapOverlapResolutionChoice =
  | "remove_conflicts"
  | "tail_existing_priority"
  | "tail_incoming_priority"
  | "truncate_incoming_priority"
  | "delay_existing_start_priority"
  | "drop_peer_conflicts";

export type OverlapCheckParams = {
  channelIDs: string[];
  startDate: Date | string;
  endDate: Date | string;
  excludeSwapUID?: string;
};

type Props = {
  visible: boolean;
  onHide: () => void;
  overlap: BannerSwapOverlapAnalysis;
  overlapCheckParams?: OverlapCheckParams;
  onConfirm: (resolution: BannerSwapOverlapResolution) => void;
  submitting?: boolean;
  confirmLabel?: string;
};

const OPTION_COPY: Record<
  SwapOverlapResolutionChoice,
  { title: string; description: string }
> = {
  remove_conflicts: {
    title: "Remove overlapping channels",
    description:
      "Drop conflicting channels and queue the swap for the rest only.",
  },
  tail_existing_priority: {
    title: "Tail after existing",
    description:
      "Existing swap finishes first; yours keeps its scheduled end date.",
  },
  tail_incoming_priority: {
    title: "Give this swap priority",
    description: "Existing swap ends early so yours runs on schedule.",
  },
  truncate_incoming_priority: {
    title: "End your swap early",
    description:
      "Your swap ends when the existing swap starts, so they no longer overlap.",
  },
  delay_existing_start_priority: {
    title: "Start the other swap late",
    description:
      "The existing swap waits until yours finishes, then runs on its original end date.",
  },
  drop_peer_conflicts: {
    title: "Remove from other swap",
    description:
      "Drop conflicting channels from the existing swap (or cancel it if none remain). Yours runs on schedule.",
  },
};

function secondaryOptionsForGeometry(
  geometry: BannerSwapOverlapGeometry | undefined
): SwapOverlapResolutionChoice[] {
  if (geometry === "incoming_starts_first") {
    return [
      "truncate_incoming_priority",
      "delay_existing_start_priority",
      "drop_peer_conflicts",
    ];
  }
  return ["tail_existing_priority", "tail_incoming_priority"];
}

function toOverlapCheckBody(params: OverlapCheckParams) {
  return {
    channelIDs: params.channelIDs,
    startDate:
      params.startDate instanceof Date
        ? params.startDate.toISOString()
        : params.startDate,
    endDate:
      params.endDate instanceof Date
        ? params.endDate.toISOString()
        : params.endDate,
    excludeSwapUID: params.excludeSwapUID,
  };
}

const SwapOverlapResolutionDialog: React.FC<Props> = ({
  visible,
  onHide,
  overlap,
  overlapCheckParams,
  onConfirm,
  submitting = false,
  confirmLabel = "Confirm and queue",
}) => {
  const [resolvedOverlap, setResolvedOverlap] =
    useState<BannerSwapOverlapAnalysis | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const displayOverlap = resolvedOverlap ?? overlap;
  const preview = displayOverlap.preview;
  const geometry = displayOverlap.overlapGeometry;
  const [choice, setChoice] = useState<SwapOverlapResolutionChoice>(
    "remove_conflicts"
  );

  const options = useMemo<SwapOverlapResolutionChoice[]>(
    () => ["remove_conflicts", ...secondaryOptionsForGeometry(geometry)],
    [geometry]
  );

  useEffect(() => {
    if (!visible) {
      setResolvedOverlap(null);
      setFetchError(null);
      return;
    }
    setChoice("remove_conflicts");
    if (!overlapCheckParams) {
      setResolvedOverlap(overlap);
      return;
    }

    let cancelled = false;
    setFetching(true);
    setFetchError(null);
    void bannerDashboardApi
      .postBannerSwapOverlapCheck(toOverlapCheckBody(overlapCheckParams))
      .then((data) => {
        if (!cancelled) {
          setResolvedOverlap(data);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Failed to load overlap preview.";
        setFetchError(msg);
        setResolvedOverlap(overlap);
      })
      .finally(() => {
        if (!cancelled) {
          setFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [visible, overlapCheckParams, overlap]);

  const selectedPreview = preview?.[choice];

  const isSelectedAvailable = selectedPreview?.available !== false;

  return (
    <Dialog
      header="Schedule overlap detected"
      visible={visible}
      onHide={onHide}
      className="overlap-resolution-dialog"
      modal
      dismissableMask={!submitting}
      closable={!submitting}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            onClick={onHide}
            disabled={submitting}
          />
          <Button
            label={confirmLabel}
            onClick={() => onConfirm({ strategy: choice })}
            disabled={!isSelectedAvailable || submitting || fetching}
            loading={submitting}
          />
        </div>
      }
    >
      <p className="text-sm text-gray-700 mb-3">
        This swap overlaps with existing scheduled swaps. Pick a resolution —
        each option shows what will happen on conflict channels.
      </p>

      {fetching ? (
        <div className="flex justify-center py-6">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      ) : null}
      {fetchError ? (
        <p className="text-sm text-amber-700 mb-3">
          Could not refresh overlap preview from the server. Showing cached
          analysis ({fetchError}).
        </p>
      ) : null}

      {!fetching ? (
        <>
      <OverlapConflictPreview overlap={displayOverlap} />

      <div className="border border-gray-200 rounded-md overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 font-medium">Channel</th>
              <th className="text-left p-2 font-medium">Conflicts with</th>
            </tr>
          </thead>
          <tbody>
            {displayOverlap.conflicts.map((conflict) => (
              <tr key={conflict.channelId} className="border-t border-gray-100">
                <td className="p-2 align-top font-mono text-xs">
                  {conflict.channelId}
                </td>
                <td className="p-2 align-top">
                  <ul className="space-y-1">
                    {conflict.conflictingSwaps.map((swap) => (
                      <li key={`${conflict.channelId}-${swap.swapUID}`}>
                        <span className="font-medium">
                          #{swap.swapUID} {swap.swapName}
                        </span>
                        <span className="text-gray-600">
                          {" "}
                          ({formatSwapDateCell(swap.startDate)} –{" "}
                          {formatSwapDateCell(swap.endDate)})
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className={`overlap-resolution-dialog__options${
          options.length > 3
            ? " overlap-resolution-dialog__options--three-secondary"
            : ""
        }`}
      >
        {options.map((option) => {
          const meta = OPTION_COPY[option];
          const optionPreview = preview?.[option];
          const chip = OPTION_CHIP[option];
          const disabled = optionPreview?.available === false;
          const selected = choice === option;

          return (
            <label
              key={option}
              className={`overlap-resolution-dialog__option-card${
                selected ? " overlap-resolution-dialog__option-card--selected" : ""
              }${disabled ? " overlap-resolution-dialog__option-card--disabled" : ""}`}
            >
              <div className="overlap-resolution-dialog__option-header">
                <RadioButton
                  inputId={`overlap-${option}`}
                  name="overlapResolution"
                  value={option}
                  checked={selected}
                  onChange={(e) =>
                    setChoice(e.value as SwapOverlapResolutionChoice)
                  }
                  disabled={disabled || submitting}
                />
                <span
                  className={`overlap-resolution-dialog__option-chip overlap-resolution-dialog__option-chip--${chip.tone}`}
                  aria-hidden
                >
                  {chip.letter}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="overlap-resolution-dialog__option-title">
                    {meta.title}
                  </div>
                  <div className="overlap-resolution-dialog__option-desc">
                    {meta.description}
                  </div>
                </div>
              </div>

              <OverlapResolutionDiagram
                option={option}
                overlap={displayOverlap}
                compact
              />

              <OverlapResolutionCalloutList
                callouts={buildOverlapResolutionCallouts(option, displayOverlap)}
              />

              {optionPreview?.unavailableReason ? (
                <div className="overlap-resolution-dialog__option-warning">
                  {optionPreview.unavailableReason}
                </div>
              ) : null}
            </label>
          );
        })}
      </div>
        </>
      ) : null}
    </Dialog>
  );
};

export default SwapOverlapResolutionDialog;
